import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const { user, token } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const listenersRef = useRef(new Set());

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Sin usuario o SIN TOKEN no se conecta: conectar sin token producía 403 del
    // backend y el frontend reintentaba en bucle (~1/5s) re-renderizando toda la
    // app. El efecto re-ejecuta connect cuando el token esté disponible.
    if (!user?.id || !token) return;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    // Evitar abrir un socket nuevo si ya hay uno abierto/conectando
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN ||
                          wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const baseUrl = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${baseUrl}/ws/${user.id}?token=${encodeURIComponent(token)}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);

          // Notify all listeners
          listenersRef.current.forEach(listener => {
            try {
              listener(data);
            } catch (err) {
              console.error('[WebSocket] Listener error:', err);
            }
          });

          // Handle specific message types with toasts
          if (data.type === 'cambio_predio') {
            const action = data.action === 'aprobado' ? '✅ aprobado' : '❌ rechazado';
            toast.info(
              `Cambio ${action} por ${data.decidido_por}`,
              {
                description: `Predio: ${data.codigo_predio}`,
                action: {
                  label: 'Sincronizar',
                  onClick: () => {
                    window.dispatchEvent(new CustomEvent('syncPredios', { detail: data }));
                  }
                },
                duration: 10000
              }
            );
          }
        } catch (err) {
          console.error('[WebSocket] Error parsing message:', err);
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        // Cierre limpio (logout) → no reconectar.
        if (event.code === 1000) return;
        // Backoff exponencial con tope de 60s para no martillar el servidor ni
        // re-renderizar la app cada pocos segundos.
        const intento = reconnectAttemptsRef.current++;
        const delay = Math.min(5000 * Math.pow(2, intento), 60000);
        reconnectTimeoutRef.current = setTimeout(() => connect(), delay);
      };

      ws.onerror = () => {
        // onclose se encarga de la reconexión con backoff.
      };
    } catch (err) {
      console.error('[WebSocket] Connection error:', err);
    }
  }, [user?.id, token]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    if (wsRef.current) {
      try { wsRef.current.close(1000, 'User logout'); } catch (e) {}
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Add a message listener
  const addListener = useCallback((callback) => {
    listenersRef.current.add(callback);
    return () => {
      listenersRef.current.delete(callback);
    };
  }, []);

  // Send a message (for ping/pong)
  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof message === 'string' ? message : JSON.stringify(message));
    }
  }, []);

  // Connect when user logs in (y cuando el token esté disponible)
  useEffect(() => {
    if (user?.id && token) {
      connect();
    } else {
      disconnect();
    }
    return () => {
      disconnect();
    };
  }, [user?.id, token, connect, disconnect]);

  // Keep-alive ping every 30 seconds
  useEffect(() => {
    if (!isConnected) return;
    const pingInterval = setInterval(() => {
      sendMessage('ping');
    }, 30000);
    return () => clearInterval(pingInterval);
  }, [isConnected, sendMessage]);

  // Memoizar el value: sin esto, cada cambio de isConnected/lastMessage creaba un
  // objeto nuevo y re-renderizaba TODOS los consumidores del contexto.
  const value = useMemo(() => ({
    isConnected,
    lastMessage,
    addListener,
    sendMessage
  }), [isConnected, lastMessage, addListener, sendMessage]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
